import { createContext, useContext, type CSSProperties, type ReactNode } from 'react'

interface PageHeadProps {
  eyebrow?: string
  title: string
  subtitle?: string
  actions?: ReactNode
}

/**
 * When true (set by a tabbed hub via {@link HubChromeProvider}), PageHead
 * renders ONLY its subtitle (+ actions) and drops the eyebrow + title. The
 * hub owns the page's hero heading, so a child page mounted as a hub tab
 * shows its descriptive subtitle under the tab bar without repeating a title
 * that would duplicate the tab label. Default false everywhere else, so this
 * is a no-op for every standalone PageHead usage.
 */
const HubChromeContext = createContext(false)

export function HubChromeProvider({ children }: { children: ReactNode }): JSX.Element {
  return <HubChromeContext.Provider value={true}>{children}</HubChromeContext.Provider>
}

const subtitleStyle: CSSProperties = {
  fontSize: 15,
  lineHeight: 1.55,
  color: 'var(--text-2)',
  maxWidth: '64ch',
}

/**
 * Page-level heading block.
 *
 * Handover §02 / §05 spec:
 *   - eyebrow : font-mono, 11px, 600, uppercase, 0.1em tracking, --ink-3
 *   - h1      : font-sans, 28px, 500, -0.02em tracking, --ink
 *   - subtitle: font-sans, 15px, 400, 1.55 line-height, --ink-2, max-width 64ch
 *   - spacing : eyebrow → 12px → h1 → 4px → subtitle → 32px margin-bottom
 *   - actions : right-aligned, flex-end of the row
 */
export function PageHead({
  eyebrow,
  title,
  subtitle,
  actions,
}: PageHeadProps): JSX.Element | null {
  const insideHub = useContext(HubChromeContext)

  // Hub tab mode: subtitle (+ actions) only. Nothing to show → render nothing
  // so the tab content isn't pushed down by an empty header.
  if (insideHub) {
    if (!subtitle && !actions) return null
    return (
      <header
        data-testid="page-head"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 24,
          marginBottom: 24,
        }}
      >
        <div>
          {subtitle ? <p style={{ ...subtitleStyle, margin: 0 }}>{subtitle}</p> : null}
        </div>
        {actions ? (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>{actions}</div>
        ) : null}
      </header>
    )
  }

  return (
    <header
      data-testid="page-head"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 24,
        marginBottom: 32,
      }}
    >
      <div>
        {eyebrow ? (
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--text-3)',
              marginBottom: 12,
            }}
          >
            {eyebrow}
          </div>
        ) : null}
        <h1
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 28,
            fontWeight: 500,
            letterSpacing: '-0.02em',
            color: 'var(--text)',
            margin: 0,
          }}
        >
          {title}
        </h1>
        {subtitle ? <p style={{ ...subtitleStyle, margin: '4px 0 0' }}>{subtitle}</p> : null}
      </div>
      {actions ? (
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>{actions}</div>
      ) : null}
    </header>
  )
}
